import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Ban,
  Camera,
  Check,
  CheckCircle2,
  Loader2,
  Send,
  Trash2,
  TriangleAlert,
  Truck,
  X,
} from "lucide-react";
import React, { Suspense, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  addStockTransferItem,
  deleteStockTransfer,
  deleteStockTransferItem,
  getStockTransfer,
  getStockTransferConfig,
  lookupStockTransferPartia,
  resolveStockTransferScan,
  saveStockTransfer,
  sendStockTransfer,
  setStockTransferItemQty,
  type ResolvedScan,
  type StockTransferDetail,
  type StockTransferHeader,
  type StockTransferItem,
  type StockTransferItemCreate,
} from "@/api/stock_transfer";
import { Button } from "@/components/ui/button";
import { scanErrorTitle } from "@/lib/api-errors";
import { fmtDateTime } from "@/lib/format";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { type OrderScan, parseScan } from "@/lib/order-scan";
import { GOODS_AND_QR_FORMATS } from "@/lib/scan-formats";
import { CharacteristicChoice, charAnswer } from "@/lib/scan-ui/characteristic-choice";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { DUPLICATE_MARK_TEXT, sameMarkIn } from "@/lib/scan-ui/marks";
import { BottomSheet, Dialog } from "@/lib/scan-ui/overlays";
import { PartiaAsk } from "@/lib/scan-ui/partia-ask";
import { QtyInput } from "@/lib/scan-ui/qty-input";
import { toResolveRequest } from "@/lib/scan-ui/scan-resolve";
import { apiErrorText, LoadState, useGoBack } from "@/lib/scan-ui/screen";
import { fmtQty } from "@/lib/scan-ui/status";
import { useScanCapture } from "@/lib/use-scan-capture";
import { cn } from "@/lib/utils";

/**
 * Скан-экран «Перемещения» (без заказа): скан → позиция → «Сохранить в 1С»
 * (создание И проведение `ПеремещениеЗапасов` одним действием).
 *
 * Сверять не с чем — заказа нет, поэтому экран проще проверки перемещений:
 * список позиций и их количество. Остаток сторожит бэкенд (гейт СВОБОДНОГО
 * остатка на складе-отправителе, 409 с причиной) — его текст показываем как
 * есть, плашкой, которая не исчезает сама.
 *
 * Состояния: черновик (правится) → документ создан, не проведён (`doc_ref`
 * есть, позиции ЗАМОРОЖЕНЫ: портал не должен разойтись с документом в 1С;
 * единственное действие — повторить «Сохранить», бэкенд ПРОВЕДЁТ) →
 * проведён (только чтение).
 *
 * Через приёмку (`requires_receipt`: склад-получатель или отправитель в списке
 * «Приёмки перемещений»): вместо «Сохранить в 1С» — «Отправить на
 * приёмку». В 1С ничего не пишется, позиции замораживаются; дальше —
 * получатель («Приёмка перемещений») и разбор («Расхождения перемещений»).
 * Отправлено / расхождение / принято / отменено — только чтение со статусом.
 */

interface Draft {
  scan: OrderScan;
  resolved: ResolvedScan;
  charRef: string;
  charName: string;
  withoutChar: boolean;
  partiaRef: string;
  partiaName: string;
  partiaSource: string;
  withoutPartia: boolean;
  /** Единицы скана (коробов/штук); в базовых — `units × коэффициент`. */
  units: number;
}

type Banner =
  | { tone: "error"; title: string; detail: string; raw?: string }
  | { tone: "ok"; text: string };

/** Коэффициент единицы позиции: сервер хранит единицы скана и базовые. */
function itemCoef(it: StockTransferItem): number {
  return it.qty_scanned > 0 ? it.qty_base / it.qty_scanned : 1;
}

/** Коэффициент из резолва — той же формой, что `itemCoef`, а не `unit_coefficient`
 * напрямую: для ЧЗ движок форсит `qty_scanned=qty_base=1` (штука есть штука,
 * каким бы коробом её ни промаркировали, `engine.py` ~492-495), а
 * `unit_coefficient` при этом остаётся коэффициентом найденной в регистре
 * единицы и может быть, например, 6. Домножение на него превращало один
 * отсканированный ЧЗ-экземпляр в шесть базовых штук. */
function resolvedCoef(r: ResolvedScan): number {
  return r.qty_scanned > 0 ? r.qty_base / r.qty_scanned : 1;
}

/** «10 шт»; единицы нет — просто число (слово наугад не подставляем:
 *  базовая единица номенклатуры в 1С может быть «упак», а не «шт»). */
function withUnit(qty: number, unit: string | null | undefined): string {
  return unit ? `${fmtQty(qty)} ${unit}` : fmtQty(qty);
}

/** Единица ИТОГА — только если у ВСЕХ строк она одна и непустая; иначе "". */
function commonUnit(units: (string | null | undefined)[]): string {
  const names = new Set(units.map((u) => u ?? ""));
  return names.size === 1 ? ([...names][0] ?? "") : "";
}

export function StockTransferSessionPage(): React.ReactElement {
  // Чанк камеры — при ВХОДЕ: устаревший чанк после выката перезагружает
  // страницу, и в момент тапа «Камера» это прервало бы работу.
  usePreloadChunk(CameraScanner);
  const { sessionId = "" } = useParams<{ sessionId: string }>();
  const goBack = useGoBack("/stock-transfer");
  const detailQ = useQuery({
    queryKey: ["stock-transfer", sessionId],
    queryFn: () => getStockTransfer(sessionId),
    enabled: !!sessionId,
  });
  const d = detailQ.data;

  return (
    <div className="mx-auto flex h-app max-w-md flex-col bg-slate-50">
      {/* Шапка — ВСЕГДА, даже при сбое загрузки: иммерсивный роут без неё тупик. */}
      <div className="bg-slate-900 text-white">
        <div className="flex h-[46px] items-center gap-1 px-1">
          <button
            type="button"
            onClick={goBack}
            className="flex size-9 items-center justify-center"
            aria-label="Назад"
          >
            <ArrowLeft className="size-5" />
          </button>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-[13px] font-bold">
              {d ? sessionTitle(d.header) : "Перемещение"}
            </div>
            {d && (
              <div className="truncate text-[11px] text-slate-300">
                {d.header.from_wh_name} → {d.header.to_wh_name}
              </div>
            )}
          </div>
        </div>
        {d && (
          <div className="truncate border-t border-white/10 px-3 py-1 text-[11px] text-slate-400">
            {d.header.organization_name}
          </div>
        )}
      </div>
      {d ? (
        <SessionBody sessionId={sessionId} detail={d} />
      ) : (
        <LoadState
          isLoading={detailQ.isLoading}
          error={detailQ.error}
          onRetry={() => void detailQ.refetch()}
        />
      )}
    </div>
  );
}

/** Заголовок экрана: номер документа 1С, если он есть; иначе номер портала —
 *  «черновик» только у открытого (отправленное черновиком уже не назвать). */
function sessionTitle(h: StockTransferHeader): string {
  if (h.doc_number) return `Перемещение № ${h.doc_number}`;
  if (h.status === "open") return `Перемещение (черновик № ${h.display_number ?? "—"})`;
  return `Перемещение №${h.display_number ?? "—"}`;
}

function SessionBody({
  sessionId,
  detail,
}: {
  sessionId: string;
  detail: StockTransferDetail;
}): React.ReactElement {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const detailKey = ["stock-transfer", sessionId];
  const refetch = () => qc.invalidateQueries({ queryKey: detailKey });

  const configQ = useQuery({
    queryKey: ["stock-transfer-config"],
    queryFn: getStockTransferConfig,
    staleTime: 5 * 60_000,
  });
  const scanMethod = configQ.data?.scan_method;
  const cameraAllowed = scanMethod === "camera" || scanMethod === "both";

  const h = detail.header;
  // Всё, кроме открытого, — только чтение: отправленное заморожено (бэк
  // отбивает правки 409), принятое/отменённое — итог.
  const readOnly = h.status !== "open";
  const frozen = h.status === "open" && !!h.doc_ref;
  const editable = !readOnly && !frozen;
  // Документа ещё нет — его создаст приёмка получателя, «Сохранить в 1С» нет.
  const viaReceipt = h.requires_receipt && !h.doc_ref;

  const [banner, setBanner] = useState<Banner | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dialog, setDialog] = useState<
    | null
    | { kind: "save" }
    | { kind: "send" }
    | { kind: "delete-session" }
    | { kind: "delete-item"; item: StockTransferItem }
  >(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  // Отказ «Сохранить»/«Отправить» — плашкой над кнопкой, текстом бэка как есть.
  const [actionError, setActionError] = useState<{ title: string; text: string } | null>(null);
  const resolvingRef = useRef(false);
  const sendingRef = useRef(false);

  const errorBanner = (title: string, e: unknown, raw?: string) => {
    setBanner({ tone: "error", title, detail: apiErrorText(e), raw });
    navigator.vibrate?.([60, 60, 60]);
  };

  const addM = useMutation({
    mutationFn: (body: StockTransferItemCreate) => addStockTransferItem(sessionId, body),
    onSuccess: (it) => {
      navigator.vibrate?.(40);
      setDraft(null);
      setBanner({
        tone: "ok",
        text: `+${fmtQty(it.qty_base)} · ${it.nomenclature_article || ""} ${it.nomenclature_name || ""}`.trim(),
      });
      void refetch();
    },
    onError: (e, body) => {
      // 409 гейта остатка — штатный исход: текст бэка называет причину
      // («свободно 0, лежит N, остальное в резерве»), он нужен, чтобы идти в 1С.
      setDraft(null);
      errorBanner("Позиция не принята", e, body.raw);
    },
  });
  const qtyM = useMutation({
    mutationFn: (v: { item: StockTransferItem; units: number }) =>
      setStockTransferItemQty(sessionId, v.item.id, v.units, itemCoef(v.item)),
    onSuccess: () => {
      setBanner(null);
      void refetch();
    },
    onError: (e) => {
      errorBanner("Количество не изменено", e);
      void refetch();
    },
  });
  const deleteItemM = useMutation({
    mutationFn: (itemId: string) => deleteStockTransferItem(sessionId, itemId),
    onSuccess: () => {
      setDialog(null);
      void refetch();
    },
    onError: (e) => {
      setDialog(null);
      errorBanner("Позиция не удалена", e);
    },
  });
  const deleteSessionM = useMutation({
    mutationFn: () => deleteStockTransfer(sessionId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["stock-transfers"] });
      navigate("/stock-transfer", { replace: true });
    },
    onError: (e) => {
      setDialog(null);
      errorBanner("Черновик не удалён", e);
      void refetch();
    },
  });
  const saveM = useMutation({
    mutationFn: () => saveStockTransfer(sessionId),
    onSuccess: (fresh) => {
      setDialog(null);
      setActionError(null);
      setBanner(null);
      qc.setQueryData(detailKey, fresh);
      void qc.invalidateQueries({ queryKey: ["stock-transfers"] });
    },
    onError: (e) => {
      // Текст — как есть: «создан, но не проведён… повтор ПРОВЕДЁТ», нехватка
      // остатка, отказ 1С, «это перемещение через приёмку». Повтор — той же
      // кнопкой; перечитываем карточку, чтобы увидеть `doc_ref`, если
      // документ успел создаться, или `requires_receipt`, если правило
      // приёмки поменялось — тогда на месте «Сохранить» встанет «Отправить».
      setDialog(null);
      setActionError({ title: "Не сохранено в 1С", text: apiErrorText(e) });
      void refetch();
    },
  });
  // «Отправить на приёмку»: в 1С ничего. Отказ (гейт остатка, уже отправлено,
  // не через приёмку) — текстом бэка; карточку перечитываем: статус или
  // правило могли поменяться.
  const sendM = useMutation({
    mutationFn: () => sendStockTransfer(sessionId),
    onSuccess: (fresh) => {
      setDialog(null);
      setActionError(null);
      setBanner(null);
      qc.setQueryData(detailKey, fresh);
      void qc.invalidateQueries({ queryKey: ["stock-transfers"] });
      void qc.invalidateQueries({ queryKey: ["transfer-receipts"] });
      toast.success("Отправлено на приёмку");
    },
    onError: (e) => {
      setDialog(null);
      setActionError({ title: "Не отправлено на приёмку", text: apiErrorText(e) });
      void refetch();
    },
    onSettled: () => {
      sendingRef.current = false;
    },
  });
  // Синхронный замок: двойной тап по «Отправить» — один POST.
  function submitSend() {
    if (sendingRef.current) return;
    sendingRef.current = true;
    sendM.mutate();
  }

  const busy =
    resolving ||
    addM.isPending ||
    qtyM.isPending ||
    deleteItemM.isPending ||
    saveM.isPending ||
    sendM.isPending ||
    deleteSessionM.isPending;
  const scanEnabled = editable && !draft && !dialog && !cameraOpen && !busy;

  // Перехват сам молчит при `!scanEnabled`; здесь — те же условия для
  // камеры (её скан приходит в момент закрытия, мимо перехвата).
  async function handleScan(raw: string) {
    if (!editable || draft || dialog || resolvingRef.current) return;
    resolvingRef.current = true;
    setResolving(true);
    try {
      await processScan(raw);
    } finally {
      resolvingRef.current = false;
      setResolving(false);
    }
  }

  async function processScan(raw: string) {
    const scan = parseScan(raw);
    const req = toResolveRequest(scan);
    if (!req) {
      setBanner({
        tone: "error",
        title: "Не распознан код",
        detail: "Это не ШК/GTIN/GS1/DataMatrix товара",
        raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    // Та же штука ЧЗ второй раз в сессию — отказ сразу, без 1С (бэк — 409).
    if (sameMarkIn(scan, detail.items)) {
      setBanner({ tone: "error", title: "Уже отсканировано", detail: DUPLICATE_MARK_TEXT, raw });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    let resolved: ResolvedScan;
    try {
      resolved = await resolveStockTransferScan(sessionId, req);
    } catch (e) {
      errorBanner(scanErrorTitle(e, "Ошибка 1С"), e, raw);
      return;
    }
    if (!resolved.matched) {
      setBanner({
        tone: "error",
        // Коробку 1С даже не спрашивали — «товар не найден в 1С» про неё неправда.
        title: resolved.scan_kind === "sscc" ? "Коробку не принять" : "Товар не найден в 1С",
        detail: resolved.message || "Код не найден в 1С",
        raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    // Вскрытый короб упаковки: принимается ОСТАТОК — сказать об этом сразу.
    if (resolved.notice) toast.warning(resolved.notice, { duration: 10000 });
    const next: Draft = {
      scan,
      resolved,
      charRef: resolved.characteristic_ref,
      charName: resolved.characteristic_name,
      withoutChar: false,
      partiaRef: detail.partia_enabled ? resolved.partia_ref : "",
      partiaName: detail.partia_enabled ? resolved.partia_name : "",
      partiaSource: detail.partia_enabled ? resolved.partia_source : "",
      withoutPartia: false,
      units: resolved.qty_scanned || 1,
    };
    if (needsChar(next) || needsPartia(next, detail.partia_enabled)) {
      setBanner(null);
      setDraft(next);
      return;
    }
    addM.mutate(itemBody(next, detail.partia_enabled));
  }

  const capture = useScanCapture({
    enabled: scanEnabled,
    onScan: (raw) => void handleScan(raw),
    // Код пришёл, пока перехват выключен (летит прошлый скан, открыто окно):
    // не теряем молча — называем, что он не принят.
    onBlocked: (raw) => {
      if (!editable) {
        setBanner({
          tone: "error",
          title: "Скан не принят",
          detail:
            "Перемещение отправлено или закрыто — позиции менять нельзя. Нужно ещё — создайте новое перемещение.",
          raw,
        });
        return;
      }
      setBanner({
        tone: "error",
        title: "Скан не принят",
        detail: busy
          ? "Предыдущий скан ещё обрабатывается — отсканируйте ещё раз."
          : "Сначала закройте открытое окно.",
        raw,
      });
    },
  });

  const items = [...detail.items].sort((a, b) => b.created_at.localeCompare(a.created_at));

  return (
    <>
      <input
        ref={capture.scanInputRef}
        {...capture.scanInputProps}
        className="sr-only"
      />

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {readOnly && <StatusPanel h={h} />}
        {frozen && (
          <div className="rounded-xl border-2 border-red-300 bg-red-50 p-3 text-[13px] text-red-900">
            <div className="font-bold">
              Документ № {h.doc_number ?? "—"} создан в 1С, но НЕ проведён
            </div>
            <div className="mt-0.5 text-[12px]">
              Позиции заморожены, чтобы не разойтись с документом. Нажмите «Провести в
              1С» — повтор проведёт уже созданный документ.
            </div>
          </div>
        )}

        {configQ.isError && editable && (
          <p className="rounded border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-900">
            Способ сканирования не загрузился: {apiErrorText(configQ.error)}. Камера
            недоступна, сканер ТСД работает.
          </p>
        )}

        {banner?.tone === "error" && (
          <div className="rounded-xl border-2 border-red-300 bg-red-50 p-3" role="alert">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-[17px] font-bold text-red-700">{banner.title}</div>
                <div className="mt-1 text-[13px] text-red-900">{banner.detail}</div>
                {banner.raw && (
                  <div className="mt-1 break-all font-mono text-[11px] text-slate-500">
                    {banner.raw}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setBanner(null)}
                className="flex size-8 items-center justify-center"
                aria-label="Скрыть"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        )}
        {banner?.tone === "ok" && (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-800">
            <Check className="size-4 shrink-0" />
            <span className="truncate">{banner.text}</span>
          </div>
        )}

        {editable && (
          <div className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-[13px]">
            {resolving || addM.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin text-sky-600" />
                <span className="text-sky-700">Ищем в 1С…</span>
              </>
            ) : (
              <span className="text-slate-500">Сканируйте товар</span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between px-1 text-[12px] text-slate-500">
          <span>Позиций: {h.items_count}</span>
          <span>Кол-во: {withUnit(h.qty_base_total, commonUnit(items.map((i) => i.base_unit_name)))}</span>
        </div>

        {items.map((it) => (
          <ItemRow
            key={`${it.id}:${it.qty_scanned}`}
            item={it}
            editable={editable && !saveM.isPending && !sendM.isPending}
            busy={qtyM.isPending && qtyM.variables?.item.id === it.id}
            onSetUnits={(units) => qtyM.mutate({ item: it, units })}
            onDelete={() => setDialog({ kind: "delete-item", item: it })}
          />
        ))}
        {items.length === 0 && (
          <p className="py-6 text-center text-[13px] text-slate-400">Позиций пока нет</p>
        )}
      </div>

      {!readOnly && (
        <div className="space-y-2 border-t bg-white p-2">
          {actionError && (
            <div className="rounded-lg border-2 border-red-300 bg-red-50 p-2" role="alert">
              <div className="text-[14px] font-bold text-red-700">{actionError.title}</div>
              <div className="whitespace-pre-line text-[12px] text-red-900">{actionError.text}</div>
            </div>
          )}
          {viaReceipt ? (
            <button
              type="button"
              disabled={detail.items.length === 0 || busy}
              onClick={() => setDialog({ kind: "send" })}
              className={cn(
                "flex h-14 w-full flex-col items-center justify-center rounded-[10px] border text-[15px] font-bold leading-tight",
                detail.items.length === 0
                  ? "border-slate-200 bg-slate-100 text-slate-500"
                  : "border-sky-700 bg-sky-700 text-white disabled:opacity-60",
              )}
            >
              <span className="flex items-center gap-2">
                {sendM.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  detail.items.length > 0 && <Send className="size-4" />
                )}
                {detail.items.length === 0
                  ? "Отсканируйте товары"
                  : sendM.isPending
                    ? "Отправляем…"
                    : "Отправить на приёмку"}
              </span>
              {detail.items.length > 0 && !sendM.isPending && (
                <span className="text-[11px] font-semibold opacity-90">в 1С — после приёмки</span>
              )}
            </button>
          ) : (
            <Button
              className="h-14 w-full bg-emerald-600 text-[15px] font-bold hover:bg-emerald-700"
              disabled={saveM.isPending || (editable && detail.items.length === 0) || busy}
              onClick={() => setDialog({ kind: "save" })}
            >
              {saveM.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Сохраняем в 1С…
                </>
              ) : frozen ? (
                "Провести в 1С"
              ) : (
                "Сохранить в 1С"
              )}
            </Button>
          )}
          {editable && (
            <Button
              variant="outline"
              className="h-10 w-full border-red-300 text-red-700"
              disabled={busy}
              onClick={() => setDialog({ kind: "delete-session" })}
            >
              <Trash2 className="size-4" /> Удалить черновик
            </Button>
          )}
        </div>
      )}

      {editable && cameraAllowed && !cameraOpen && (
        <button
          type="button"
          onClick={() => setCameraOpen(true)}
          className="fixed bottom-36 right-4 z-30 flex h-14 items-center gap-2 rounded-full bg-sky-700 px-5 text-[14px] font-bold text-white shadow-lg"
        >
          <Camera className="size-5" /> Камера
        </button>
      )}
      {cameraOpen && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-40 bg-black">
            <CameraScanner
              // На складе рядом и заводские EAN13/DataMatrix, и QR-наклейки
              // приёмки — без QR в наборе камера их не видела бы.
              formats={GOODS_AND_QR_FORMATS}
              onDetect={(raw) => {
                if (busy) return;
                setCameraOpen(false);
                void handleScan(raw);
              }}
              onClose={() => setCameraOpen(false)}
            />
          </div>
        </Suspense>
      )}

      {draft && (
        <ClarifySheet
          sessionId={sessionId}
          draft={draft}
          setDraft={setDraft}
          partiaEnabled={detail.partia_enabled}
          busy={addM.isPending}
          onCancel={() => setDraft(null)}
          onConfirm={() => addM.mutate(itemBody(draft, detail.partia_enabled))}
        />
      )}

      {dialog?.kind === "save" && (
        <Dialog
          title={frozen ? "Провести документ в 1С?" : "Сохранить перемещение в 1С?"}
          body={
            frozen
              ? `Документ № ${h.doc_number ?? "—"} уже создан — он будет проведён.`
              : `В 1С будет создан и проведён документ «Перемещение запасов»: ${h.from_wh_name} → ${h.to_wh_name}, позиций ${h.items_count}. После этого правка невозможна.`
          }
          confirmLabel={frozen ? "Провести" : "Сохранить"}
          confirmBusy={saveM.isPending}
          onConfirm={() => saveM.mutate()}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "send" && (
        <Dialog
          title="Отправить на приёмку?"
          body={
            <div className="space-y-1">
              <div>
                {h.from_wh_name} → <b>{h.to_wh_name}</b>
              </div>
              <div>
                Позиций: <b>{detail.items.length}</b> · всего{" "}
                <b>
                  {withUnit(
                    detail.items.reduce((sum, it) => sum + it.qty_base, 0),
                    commonUnit(detail.items.map((it) => it.base_unit_name)),
                  )}
                </b>
              </div>
              <div className="font-semibold text-slate-700">
                В 1С ничего не запишется; документ создаст приёмка склада «{h.to_wh_name}».
                Менять позиции после отправки нельзя.
              </div>
            </div>
          }
          confirmLabel="Отправить"
          confirmBusy={sendM.isPending}
          onConfirm={submitSend}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "delete-item" && (
        <Dialog
          title="Удалить позицию?"
          body={`${dialog.item.nomenclature_article ?? ""} ${dialog.item.nomenclature_name ?? ""} — ${withUnit(dialog.item.qty_base, dialog.item.base_unit_name)}`}
          confirmLabel="Удалить"
          confirmBusy={deleteItemM.isPending}
          danger
          onConfirm={() => deleteItemM.mutate(dialog.item.id)}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "delete-session" && (
        <Dialog
          title="Удалить черновик?"
          body="Все отсканированные позиции будут удалены. В 1С ничего не создавалось."
          confirmLabel="Удалить"
          confirmBusy={deleteSessionM.isPending}
          danger
          onConfirm={() => deleteSessionM.mutate()}
          onCancel={() => setDialog(null)}
        />
      )}
    </>
  );
}

/**
 * Статус неоткрытого перемещения — что с ним сейчас и чей ход. Через приёмку
 * документ 1С создаёт и проводит получатель (или разбор расхождения), поэтому
 * «принято, не проведено» здесь — без кнопки: доводит приёмка/разбор.
 */
function StatusPanel({ h }: { h: StockTransferHeader }): React.ReactElement {
  const box = "flex items-start gap-2 rounded-xl border-2 p-3";
  if (h.status === "completed") {
    const posted = !!h.doc_posted_at;
    if (!h.requires_receipt || posted) {
      return (
        <div data-testid="stock-transfer-status" className={cn(box, "border-emerald-300 bg-emerald-50")}>
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-600" />
          <div className="min-w-0 text-[14px] text-emerald-900">
            <div className="font-bold">
              {h.requires_receipt
                ? `Принято · документ № ${h.doc_number ?? "—"}`
                : `Документ № ${h.doc_number ?? "—"} проведён`}
            </div>
            {h.resolution === "by_receipt" && (
              <div className="text-[12px] font-semibold">
                Проведено по принятому получателем — количества в документе могут отличаться от
                отправленных.
              </div>
            )}
            <div className="text-[12px]">
              {h.requires_receipt
                ? `Принял склад «${h.to_wh_name}»${h.received_at ? ` · ${fmtDateTime(h.received_at)}` : ""}. «Перемещение запасов» в 1С проведено.`
                : "«Перемещение запасов» в 1С. Экран только для чтения."}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div data-testid="stock-transfer-status" className={cn(box, "border-amber-300 bg-amber-50")}>
        <TriangleAlert className="mt-0.5 size-5 shrink-0 text-amber-600" />
        <div className="min-w-0 text-[14px] text-amber-900">
          <div className="font-bold">Принято · документ № {h.doc_number ?? "—"} — не проведено</div>
          <div className="text-[12px]">
            Документ доведёт приёмка: попросите склад «{h.to_wh_name}» нажать «Повторить» (или разбор
            расхождений).
          </div>
        </div>
      </div>
    );
  }
  if (h.status === "sent") {
    return (
      <div data-testid="stock-transfer-status" className={cn(box, "border-sky-300 bg-sky-50")}>
        <Truck className="mt-0.5 size-5 shrink-0 text-sky-700" />
        <div className="min-w-0 text-[14px] text-sky-900">
          <div className="font-bold">Отправлено на приёмку «{h.to_wh_name}»</div>
          <div className="text-[12px]">
            {[h.sent_by_name, fmtDateTime(h.sent_at)].filter(Boolean).join(" ") || "—"}
            {h.pending_resolution ? "" : " · ждёт получателя"}
          </div>
          {h.pending_resolution && (
            <div className="mt-1 text-[12px] font-semibold">
              Получатель принял, 1С не доведена — доделает приёмка/разбор.
            </div>
          )}
          <div className="mt-1 text-[12px]">В 1С ничего не записано. Позиции менять нельзя.</div>
        </div>
      </div>
    );
  }
  if (h.status === "discrepancy") {
    return (
      <div data-testid="stock-transfer-status" className={cn(box, "border-amber-300 bg-amber-50")}>
        <TriangleAlert className="mt-0.5 size-5 shrink-0 text-amber-600" />
        <div className="min-w-0 text-[14px] text-amber-900">
          <div className="font-bold">Расхождение — на разборе</div>
          <div className="text-[12px]">
            Принятое складом «{h.to_wh_name}» не совпало с отправленным. В 1С ничего не записано, пока
            расхождение не разберут («Расхождения перемещений»).
          </div>
          {h.pending_resolution && (
            <div className="mt-1 text-[12px] font-semibold">
              Решено провести по принятому, 1С не доведена — доделает разбор расхождений.
            </div>
          )}
        </div>
      </div>
    );
  }
  if (h.status === "cancelled") {
    return (
      <div data-testid="stock-transfer-status" className={cn(box, "border-slate-300 bg-slate-100")}>
        <Ban className="mt-0.5 size-5 shrink-0 text-slate-600" />
        <div className="min-w-0 text-[14px] text-slate-800">
          <div className="font-bold">Отменено</div>
          <div className="text-[12px]">
            В 1С по нему ничего не осталось. Нужно переместить — создайте новое перемещение.
          </div>
        </div>
      </div>
    );
  }
  // Сюда попадает только незнакомый статус — называем его, а не молчим.
  return (
    <div data-testid="stock-transfer-status" className={cn(box, "border-slate-300 bg-slate-100")}>
      <div className="text-[14px] font-bold text-slate-800">Статус перемещения: {h.status}</div>
    </div>
  );
}

function needsChar(d: Draft): boolean {
  return d.resolved.needs_characteristic && !d.charRef && !d.withoutChar;
}

/** Партию спрашиваем, только если бэкенд её требует И инсталляция их ведёт. */
function needsPartia(d: Draft, partiaEnabled: boolean): boolean {
  return partiaEnabled && d.resolved.needs_partia && !d.partiaRef && !d.withoutPartia;
}

function itemBody(d: Draft, partiaEnabled: boolean): StockTransferItemCreate {
  const r = d.resolved;
  const coef = resolvedCoef(r);
  const partiaRef = partiaEnabled && !d.withoutPartia ? d.partiaRef : "";
  return {
    scan_kind: r.scan_kind,
    raw: d.scan.raw,
    ean13: d.scan.ean13 ?? null,
    gtin: d.scan.gtin ?? null,
    serial: d.scan.serial ?? null,
    nomenclature_ref: r.nomenclature_ref,
    nomenclature_name: r.nomenclature_name,
    nomenclature_article: r.nomenclature_article,
    characteristic_ref: d.withoutChar ? "" : d.charRef,
    characteristic_name: d.withoutChar ? "" : d.charName,
    partia_ref: partiaRef,
    partia_name: partiaRef ? d.partiaName : "",
    partia_source: partiaRef ? d.partiaSource || "manual" : "none",
    unit_ref: r.unit_ref,
    unit_coefficient: coef,
    qty_scanned: d.units,
    qty_base: d.units * coef,
  };
}

function ItemRow({
  item,
  editable,
  busy,
  onSetUnits,
  onDelete,
}: {
  item: StockTransferItem;
  editable: boolean;
  busy: boolean;
  onSetUnits: (units: number) => void;
  onDelete: () => void;
}): React.ReactElement {
  const coef = itemCoef(item);
  const unit = item.base_unit_name ?? "";
  const [units, setUnits] = useState(item.qty_scanned);
  const dirty = units !== item.qty_scanned;
  return (
    <div className="rounded-lg border bg-white p-2" data-testid="stock-transfer-item">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[15px] font-bold">{item.nomenclature_article}</span>
            {coef > 1 && (
              <span className="rounded border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">
                короб ×{fmtQty(coef)}
              </span>
            )}
          </div>
          <div className="line-clamp-2 text-[12px] text-slate-600">{item.nomenclature_name}</div>
          {(item.characteristic_name || item.partia_name) && (
            <div className="text-[11px] text-slate-500">
              {[item.characteristic_name, item.partia_name && `партия ${item.partia_name}`]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
        </div>
        {editable && (
          <button
            type="button"
            onClick={onDelete}
            className="flex size-9 items-center justify-center rounded-lg border text-red-600"
            aria-label="Удалить позицию"
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[12px] text-slate-500">
          {coef > 1 ? `коробов (= ${withUnit(item.qty_base, unit)})` : unit || "количество"}
        </span>
        {editable ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="size-9 rounded-lg border text-[20px]"
              disabled={busy || item.qty_scanned <= 1}
              onClick={() => onSetUnits(item.qty_scanned - 1)}
              aria-label="Меньше"
            >
              −
            </button>
            <QtyInput
              value={units}
              min={1}
              onChange={setUnits}
              onEnter={() => dirty && onSetUnits(units)}
            />
            <button
              type="button"
              className="size-9 rounded-lg border text-[20px]"
              disabled={busy}
              onClick={() => onSetUnits(item.qty_scanned + 1)}
              aria-label="Больше"
            >
              +
            </button>
          </div>
        ) : (
          <span className="font-mono text-[18px] font-bold">
            {/* Единица — только у штучной строки: у коробочной число это
                КОРОБЫ, а базовую единицу подписывать к ним нельзя. */}
            {withUnit(item.qty_scanned, coef === 1 ? unit : "")}
          </span>
        )}
      </div>
      {editable && dirty && (
        <Button
          className="mt-2 h-10 w-full"
          disabled={busy}
          onClick={() => onSetUnits(units)}
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}{" "}
          Готово — {withUnit(units, coef === 1 ? unit : "")}
        </Button>
      )}
    </div>
  );
}

function ClarifySheet({
  sessionId,
  draft,
  setDraft,
  partiaEnabled,
  busy,
  onCancel,
  onConfirm,
}: {
  sessionId: string;
  draft: Draft;
  setDraft: (d: Draft) => void;
  partiaEnabled: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  const r = draft.resolved;
  const coef = resolvedCoef(r);
  const [partiaInput, setPartiaInput] = useState(draft.partiaName || draft.scan.batch || "");
  const [partiaError, setPartiaError] = useState<string | null>(null);
  const partiaM = useMutation({
    mutationFn: (name: string) => lookupStockTransferPartia(sessionId, r.nomenclature_ref, name),
    onSuccess: (p) => {
      setPartiaError(null);
      setDraft({
        ...draft,
        partiaRef: p.ref,
        partiaName: p.name,
        partiaSource: "manual",
        withoutPartia: false,
      });
    },
    onError: (e) => setPartiaError(apiErrorText(e)),
  });
  const askPartia = partiaEnabled && r.needs_partia;
  const charMissing = needsChar(draft);
  const partiaMissing = needsPartia(draft, partiaEnabled);
  const reason = charMissing
    ? "Выберите характеристику"
    : partiaMissing
      ? "Укажите партию или «без партии»"
      : draft.units <= 0
        ? "Укажите количество"
        : "";

  return (
    <BottomSheet onClose={onCancel} panelClassName="max-h-[85%] overflow-y-auto">
      <div className="flex items-start gap-2 p-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[18px] font-bold">{r.nomenclature_article}</span>
            {coef > 1 && (
              <span className="rounded border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">
                короб ×{fmtQty(coef)}
              </span>
            )}
          </div>
          <div className="text-[12px] text-slate-500 line-clamp-2">{r.nomenclature_name}</div>
          {/* ⚠️ Партию со скана (AI10/ЧЗ), найденную в 1С, резолвер отдаёт с
              `needs_partia=false` — `PartiaAsk` тогда не рисуется, и шторка
              молчала о партии, которая уедет в позицию. Показываем ровно то,
              что возьмёт `itemBody`. */}
          {partiaEnabled && !askPartia && draft.partiaRef && (
            <div className="mt-0.5 text-[13px] font-semibold text-slate-700" data-testid="clarify-partia">
              Партия: {draft.partiaName || "—"}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="flex size-8 items-center justify-center"
          aria-label="Закрыть"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="space-y-3 px-3 pb-3">
        <CharacteristicChoice
          options={r.available_characteristics}
          answer={{ charRef: draft.charRef, charName: draft.charName, withoutChar: draft.withoutChar }}
          onPick={(c) => setDraft({ ...draft, ...charAnswer(c) })}
          required={charMissing}
        />
        {askPartia && (
          <>
            <PartiaAsk
              value={partiaInput}
              onChange={setPartiaInput}
              hasPicked={!!draft.partiaRef}
              picked={draft.partiaName}
              onReset={() => setDraft({ ...draft, partiaRef: "", partiaName: "", partiaSource: "" })}
              onLookup={(name) => partiaM.mutate(name)}
              busy={partiaM.isPending}
              withoutPartia={draft.withoutPartia}
              onWithoutPartia={(v) => setDraft({ ...draft, withoutPartia: v })}
            />
            {partiaError && <p className="text-[12px] font-medium text-red-700">{partiaError}</p>}
          </>
        )}
        <div className="rounded-lg border p-2">
          <div className="mb-1.5 text-[12px] font-semibold text-slate-500">
            {coef > 1 ? "Коробов:" : "Количество:"}
          </div>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              className="size-12 rounded-lg border text-[22px]"
              onClick={() => setDraft({ ...draft, units: Math.max(1, draft.units - 1) })}
            >
              −
            </button>
            <QtyInput
              value={draft.units}
              min={1}
              onChange={(v) => setDraft({ ...draft, units: v })}
            />
            <button
              type="button"
              className="size-12 rounded-lg border text-[22px]"
              onClick={() => setDraft({ ...draft, units: draft.units + 1 })}
            >
              +
            </button>
          </div>
          {coef > 1 && (
            <div className="mt-1 text-center text-[11px] text-slate-500">
              = {fmtQty(draft.units * coef)} в базовых единицах
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2 border-t p-3">
        <Button variant="outline" className="h-[52px] flex-[0.4]" onClick={onCancel}>
          Отмена
        </Button>
        <Button
          className={cn("h-[52px] flex-1 bg-emerald-600 hover:bg-emerald-700")}
          disabled={!!reason || busy}
          onClick={onConfirm}
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}{" "}
          {reason || "Добавить"}
        </Button>
      </div>
    </BottomSheet>
  );
}
